import type { ReactNode } from 'react';

/**
 * The print document's building blocks, copied from production's three
 * `*-report-print.tsx` pages.
 *
 * Production's print output is a **paginated document**: a cover, then one
 * titled page per topic, each breaking to a new sheet. Every page carries a
 * small eyebrow above its title. The class names are production's so the two
 * documents can be compared side by side.
 */
export function PrintPage({
  label,
  title,
  variant,
  children,
  testId,
}: {
  /** The eyebrow above the title. Production's cover has neither. */
  label?: string;
  title?: string;
  variant?: 'cover' | 'final-page';
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      className={`page${variant ? ` ${variant}` : ''}`}
      data-testid={testId ?? 'print-page'}
      {...(title ? { 'data-page-title': title } : {})}
    >
      {label ? <div className="page-label">{label}</div> : null}
      {title ? <h1 className="page-title">{title}</h1> : null}
      {children}
    </div>
  );
}

/** The on-screen control strip. Production hides it on paper. */
export function PrintBar({ children }: { children: ReactNode }) {
  return (
    <div
      className="print-bar flex flex-wrap items-center gap-3 print:hidden"
      data-testid="print-bar"
    >
      {children}
    </div>
  );
}

/** Production's closing line, on screen only. */
export function PrintFooter() {
  return (
    <div className="print-page-footer" data-testid="print-footer">
      Created at High Performer OS
    </div>
  );
}

/** A bordered block inside a page. `card` is production's break-avoid hook. */
export function PrintCard({
  children,
  className = '',
  testId,
}: {
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      className={`card rounded-lg border border-border p-4 ${className}`}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {children}
    </div>
  );
}

/** The small uppercase label production sets above a block of prose. */
export function PrintLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
      {children}
    </p>
  );
}
